/**
 * Integrations — GoHighLevel (`/integrations/ghl`, TMMRSL-0030, H §5): the reseller's GHL
 * connection on saas-auth, OWNER only (the nav already hides it; a non-OWNER who reaches the
 * route sees an "Owner only" panel and nothing is called).
 *
 *  - `GET /ghl-integration` → the location id and the token's state, masked (`…wxyz`); `404` is
 *    "not connected".
 *  - Save → `PUT { locationId, privateIntegrationToken? }`. The token is a password field; a
 *    BLANK token is omitted from the body and saas-auth keeps the stored one (said in the helper
 *    text). `409 GHL_LOCATION_ALREADY_CLAIMED` / `422 GHL_LOCATION_NOT_VERIFIED` are shown inline.
 *  - Disconnect → `DELETE` (confirmed). Diagnose → `POST /diagnose`, its result shown as received.
 *  - TMMRSL-0035: locationId is validated client-side as `[A-Za-z0-9_-]{1,64}`; the token needs
 *    contacts + custom-fields read scopes; a save can take up to 20 s (no client timeout).
 */
import { useCallback, useId, useRef, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { StatusChip } from '@/components/status/StatusChip';
import {
  saasAuthResellerApi,
  type GhlDiagnosis,
  type GhlIntegration,
  type GhlProbe,
} from '@/api/saasAuthReseller';
import { useIsOwner } from '@/auth/authHooks';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { ApiErrorState } from '@/components/ApiErrorState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { SubmitButton } from '@/components/SubmitButton';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useToast } from '@/components/useToast';
import { useUnsavedGuard } from '@/components/useUnsavedGuard';
import { useMemberError, type MemberError } from '@/features/members/memberErrors';
import { useRemote } from '@/components/useRemote';
import { Icon } from '@/theme/Icon';

/** saas-auth's rule for a GHL location id (a blank one is left to `required`). */
const LOCATION_ID = /^[A-Za-z0-9_-]{1,64}$/;

const FIELDS = ['locationId', 'token'] as const;

export function GhlIntegrationScreen() {
  const isOwner = useIsOwner();
  return (
    <Box>
      <PageHeader id="integrations" />
      {isOwner ? <GhlConnection /> : <OwnerOnly />}
    </Box>
  );
}

function OwnerOnly() {
  const { t } = useTranslation();
  return (
    <Paper sx={{ p: 3, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Icon name="lock" size="xl" sx={{ color: 'text.secondary' }} />
      <Box>
        <Typography variant="h2" component="h2">
          {t('ghl.ownerOnlyTitle')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {t('ghl.ownerOnlyBody')}
        </Typography>
      </Box>
    </Paper>
  );
}

function GhlConnection() {
  const { t } = useTranslation();
  const load = useCallback(() => saasAuthResellerApi.getGhl(), []);
  const { status, data, error, reload } = useRemote(load);

  if (status === 'error') return <ApiErrorState what={t('errors.what.integrations')} error={error} onRetry={reload} />;
  if (status === 'loading' && data === null) {
    return (
      <Box role="status" aria-label={t('common.loading')}>
        <Skeleton variant="rounded" height={260} />
      </Box>
    );
  }
  // Keyed by the stored state so the form restarts from it after a save or disconnect.
  const connection = data ?? null;
  return (
    <GhlForm
      key={`${connection?.locationId ?? ''}|${connection?.tokenSuffix ?? ''}`}
      connection={connection}
      onChanged={reload}
    />
  );
}

function GhlForm({
  connection,
  onChanged,
}: {
  connection: GhlIntegration | null;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const showToast = useToast();
  const describe = useMemberError();
  const locationRef = useRef<HTMLInputElement>(null);
  const tokenRef = useRef<HTMLInputElement>(null);
  const [locationId, setLocationId] = useState(connection?.locationId ?? '');
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<MemberError | null>(null);
  const fields = useFieldErrors(FIELDS, { locationId: locationRef, token: tokenRef }, saving);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosis, setDiagnosis] = useState<GhlDiagnosis | null>(null);
  const [diagnoseError, setDiagnoseError] = useState<MemberError | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const connected = connection !== null;
  const tokenSet = connection?.tokenSet === true;
  // Edited: the location id differs from the saved one, or a new token was typed (FRM-008).
  const dirty = locationId !== (connection?.locationId ?? '') || token !== '';
  const guard = useUnsavedGuard({ when: dirty, name: t('unsaved.names.ghl') });

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    const trimmedLocation = locationId.trim();
    const found: Partial<Record<(typeof FIELDS)[number], string>> = {};
    if (trimmedLocation === '') found.locationId = t('validation.ghlLocation');
    else if (!LOCATION_ID.test(trimmedLocation)) found.locationId = t('ghl.locationIdInvalid');
    if (!tokenSet && token.trim() === '') found.token = t('validation.ghlToken');
    if (Object.keys(found).length > 0) {
      setSaveError(null);
      fields.fail(found);
      return;
    }
    setSaving(true);
    setSaveError(null);
    fields.reset();
    try {
      // saas-auth defaults an omitted `active` to true — keep an existing connection's state.
      // The trimmed value that was validated (TMMRSL-0072).
      await saasAuthResellerApi.saveGhl({
        locationId: trimmedLocation,
        privateIntegrationToken: token,
        ...(connection ? { active: connection.active } : {}),
      });
      showToast(t('ghl.toast.saved'));
      setToken('');
      onChanged();
    } catch (err) {
      setSaveError(describe(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDiagnose() {
    setDiagnosing(true);
    setDiagnoseError(null);
    setDiagnosis(null);
    try {
      setDiagnosis(await saasAuthResellerApi.diagnoseGhl());
    } catch (err) {
      setDiagnoseError(describe(err));
    } finally {
      setDiagnosing(false);
    }
  }

  return (
    <Stack spacing={2.5}>
      <Paper sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h2" component="h2">
            {t('ghl.stateTitle')}
          </Typography>
          <StatusChip domain="ghl" status={connected ? 'CONNECTED' : 'NOT_CONNECTED'} />
          {connected && connection.active === false ? (
            <StatusChip domain="ghl" status="INACTIVE" />
          ) : null}
        </Stack>
        {connected ? (
          <Box component="dl" sx={{ m: 0, display: 'grid', gap: 0.5 }}>
            <Typography component="dt" variant="overline">
              {t('ghl.field.locationId')}
            </Typography>
            <Typography component="dd" sx={{ m: 0, overflowWrap: 'anywhere' }}>
              {connection.locationId ?? t('members.none')}
            </Typography>
            <Typography component="dt" variant="overline">
              {t('ghl.field.token')}
            </Typography>
            <Typography component="dd" sx={{ m: 0 }}>
              {tokenSet
                ? t('ghl.tokenStored', { suffix: connection.tokenSuffix ?? '' })
                : t('ghl.tokenMissing')}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('ghl.notConnectedBody')}
          </Typography>
        )}
      </Paper>

      <Paper
        sx={{ p: 2.5 }}
        component="form"
        noValidate
        onSubmit={(event) => void handleSave(event)}
      >
        <Typography variant="h2" component="h2" sx={{ mb: 1.5 }}>
          {connected ? t('ghl.formTitleUpdate') : t('ghl.formTitleConnect')}
        </Typography>
        <ActionErrorAlert error={saveError} sx={{ mb: 2 }} />
        <TextField
          required
          fullWidth
          margin="dense"
          label={t('ghl.field.locationId')}
          value={locationId}
          onChange={(event) => {
            setLocationId(event.target.value);
            fields.clear('locationId');
          }}
          inputRef={locationRef}
          disabled={saving}
          error={fields.errors.locationId !== undefined}
          helperText={fields.errors.locationId}
          slotProps={{ htmlInput: { maxLength: 64 } }}
        />
        <TextField
          fullWidth
          required={!tokenSet}
          margin="dense"
          type="password"
          autoComplete="new-password"
          label={t('ghl.field.token')}
          value={token}
          onChange={(event) => {
            setToken(event.target.value);
            fields.clear('token');
          }}
          inputRef={tokenRef}
          error={fields.errors.token !== undefined}
          helperText={
            fields.errors.token ?? (tokenSet ? t('ghl.tokenKeepHelp') : t('ghl.tokenNewHelp'))
          }
          disabled={saving}
          slotProps={{ htmlInput: { maxLength: 512 } }}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <SubmitButton type="submit" loading={saving}>
            {t('ghl.save')}
          </SubmitButton>
          {connected ? (
            <>
              <Button disabled={diagnosing} onClick={() => void handleDiagnose()}>
                {t('ghl.diagnose')}
              </Button>
              <Button color="error" onClick={() => setConfirmDisconnect(true)}>
                {t('ghl.disconnect')}
              </Button>
            </>
          ) : null}
        </Stack>
        {/* Always mounted, so a screen reader announces the hint when it appears. */}
        <Typography variant="body2" role="status" sx={{ mt: 1, color: 'text.secondary' }}>
          {saving ? t('ghl.savingHint') : ''}
        </Typography>
      </Paper>

      <ActionErrorAlert error={diagnoseError} />
      {diagnosis ? <DiagnosisPanel diagnosis={diagnosis} /> : null}

      <ConfirmDialog
        open={confirmDisconnect}
        title={
          connection?.locationId
            ? t('ghl.disconnectTitleNamed', { location: connection.locationId })
            : t('ghl.disconnectTitle')
        }
        consequence={t('ghl.disconnectBody')}
        confirmLabel={t('ghl.disconnect')}
        onConfirm={async () => {
          await saasAuthResellerApi.deleteGhl();
          showToast(t('ghl.toast.disconnected'));
          setDiagnosis(null);
          onChanged();
        }}
        destructive
        onCancel={() => setConfirmDisconnect(false)}
        errorMessage={(err) => describe(err).message}
      />
      {guard}
    </Stack>
  );
}

/**
 * A saas-auth code in plain words (TXT-006, TMMRSL-0073): `ghl.diagnosis.<group>.<CODE>`, or
 * `….UNKNOWN` for a code the catalog doesn't map. The raw code is never shown.
 */
function codeLabel(
  t: TFunction,
  exists: (key: string) => boolean,
  group: 'interpretation' | 'kind',
  code: string,
) {
  const key = `ghl.diagnosis.${group}.${code}`;
  return exists(key) ? t(key) : t(`ghl.diagnosis.${group}.UNKNOWN`);
}

/** How GoHighLevel answered a read: accepted, rejected with its status number, or not at all. */
function ProbeResponse({ probe }: { probe: GhlProbe }) {
  const { t } = useTranslation();
  if (probe.ok) return <>{t('ghl.diagnosis.responseValue.accepted')}</>;
  if (probe.status === null) return <>{t('ghl.diagnosis.responseValue.none')}</>;
  return (
    <>
      {t('ghl.diagnosis.responseValue.rejected')}{' '}
      <Typography component="span" variant="mono" sx={{ color: 'text.secondary' }}>
        ({probe.status})
      </Typography>
    </>
  );
}

/**
 * The diagnostic as saas-auth classified it, in plain words (TMMRSL-0073): what we found, each
 * read's response and problem from catalog maps, and GoHighLevel's own message under its name.
 */
function DiagnosisPanel({ diagnosis }: { diagnosis: GhlDiagnosis }) {
  const { t, i18n } = useTranslation();
  const exists = (key: string) => i18n.exists(key);
  const probes: Array<{ key: string; label: string; probe: GhlProbe }> = [
    { key: 'contacts', label: t('ghl.diagnosis.contacts'), probe: diagnosis.contacts },
    { key: 'customFields', label: t('ghl.diagnosis.customFields'), probe: diagnosis.customFields },
  ];
  const dash = t('members.none');
  const headingId = useId();
  return (
    <Paper sx={{ p: 2.5 }} role="region" aria-label={t('ghl.diagnosis.title')}>
      <Typography variant="h2" component="h2" id={headingId} sx={{ mb: 1.5 }}>
        {t('ghl.diagnosis.title')}
      </Typography>
      <Box component="dl" sx={{ m: 0, mb: 2, display: 'grid', gap: 0.5 }}>
        <Typography component="dt" variant="overline">
          {t('ghl.diagnosis.finding')}
        </Typography>
        <Typography component="dd" sx={{ m: 0 }}>
          {codeLabel(t, exists, 'interpretation', diagnosis.interpretation)}
        </Typography>
        <Typography component="dt" variant="overline">
          {t('ghl.field.locationId')}
        </Typography>
        <Typography component="dd" sx={{ m: 0, overflowWrap: 'anywhere' }}>
          {diagnosis.locationId ?? dash}
        </Typography>
        <Typography component="dt" variant="overline">
          {t('ghl.diagnosis.plausible')}
        </Typography>
        <Typography component="dd" sx={{ m: 0 }}>
          {diagnosis.credentialStructurallyPlausible ? t('ghl.yes') : t('ghl.no')}
        </Typography>
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-labelledby={headingId}>
          <TableHead>
            <TableRow>
              <TableCell>{t('ghl.diagnosis.read')}</TableCell>
              <TableCell>{t('ghl.diagnosis.ok')}</TableCell>
              <TableCell>{t('ghl.diagnosis.response')}</TableCell>
              <TableCell>{t('ghl.diagnosis.problem')}</TableCell>
              <TableCell>{t('ghl.diagnosis.message')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {probes.map(({ key, label, probe }) => (
              <TableRow key={key}>
                <TableCell component="th" scope="row">
                  {label}
                </TableCell>
                <TableCell>{probe.ok ? t('ghl.yes') : t('ghl.no')}</TableCell>
                <TableCell>
                  <ProbeResponse probe={probe} />
                </TableCell>
                <TableCell>
                  {probe.kind === null
                    ? dash
                    : codeLabel(t, exists, 'kind', probe.kind)}
                </TableCell>
                {/* GoHighLevel's own text, under its name, so it reads as theirs, not ours. */}
                <TableCell sx={{ overflowWrap: 'anywhere' }}>
                  {probe.upstreamMessage ?? dash}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}
